/**
 * venue_anchor.ts — WHERE THE COUNTER IS (the interiors programme, slice E, 2026-10-01). PURE: no three, no DOM, no
 * rows. Given the room a bound venue is drawn as (the visit's active DESCRIPTOR — `interior_wiring.activeInterior()`),
 * the venue kind and where the building stands, it answers in WORLD space:
 *
 *   staff     where the post's holder stands to work it (behind the counter; at the bedside of the first cot),
 *   customer  where the one being served stands (before the counter) or lies (on that cot),
 *   queue     where the next ones wait.
 *
 * THREE SOURCES, in order — the venue must WORK whatever the room is:
 *   descriptor  the planner's own anchors on the ground floor (`service-staff` / `service-customer` / `queue`;
 *               the clinic's `bedside` + `bed` pair) — proven reachable by the real capsule, envelopes kept clear;
 *   legacy      a legacy floor (the lever `atlas:interior-planner = 'off'`, a `legacy.*` archetype) carries pieces
 *               and no anchors: the points are cut from the plan's OWN counter (`counter_bar`), reception desk
 *               (`desk_terminal`) or cot (`bed_single`), tested against the shell's clearances and every drawn piece;
 *   shell       a room with no counter at all (a bound building the visit did not furnish as the venue, the plan not
 *               landed yet): three points along the back half of the room that pass `slotClear`.
 *
 * THE SERVER DOES NOT KNOW THESE (slice G mirrors the planner): its presence rule is the building's box
 * (`venue_binding.insideVenue`). These anchors are the CLIENT's reach for the E key and where it presents the staff
 * and the customer to a player standing inside.
 *
 * Frame: building-local metres, the door on −Z, yaw the plans' convention (`rotation.y = buildingYaw + yaw`; 0 faces +Z,
 * π faces the door) — `toWorld` is the visit's own.
 */
import { anchorsOfKind, pieceCorners, type DescAnchor, type DescPiece, type InteriorDescriptor } from './interior_descriptor.js';
import { slotClear, type PlanFrame } from './interior_plans.js';

export type VenueRole = 'staff' | 'customer' | 'queue';
export type VenueAnchorSource = 'descriptor' | 'legacy' | 'shell';

export interface VenueFrame {
  /** Where the building stands (the enterable's x / z / yaw / groundY). */
  x: number; z: number; yaw: number; groundY: number;
  /** `interiorSpec`'s half extents and stair column (the plan frame). */
  hw: number; hd: number; stair: { x: number; z: number; d: number; w?: number };
}

export interface WorldAnchor {
  role: VenueRole;
  /** The descriptor's anchor id, or `legacy/<role>#n` / `shell/<role>#n`. */
  id: string;
  floor: number;
  /** Where the user STANDS (world); `y` = the floor slab. */
  x: number; y: number; z: number;
  /** The yaw the user faces (`rotation.y`, world). */
  yaw: number;
  /** The thing used — the counter point, the cot (world). */
  tx: number; tz: number;
  /** The same in building-local metres (the debug overlay, the gate). */
  lx: number; lz: number; facing: number;
  /** A body that SITS or LIES here (the reception chair, the cot): where it goes, world. */
  seat?: { pose: 'sit' | 'lie'; x: number; z: number; yaw: number; eye: number };
  /** The piece the anchor belongs to (a descriptor anchor), when it has one. */
  piece?: string;
  source: VenueAnchorSource;
}
export interface VenueAnchors { kind: string; source: VenueAnchorSource; staff: WorldAnchor[]; customer: WorldAnchor[]; queue: WorldAnchor[] }

/** The slab y of a floor as the furnisher seats it (0.08 on the ground floor; `FLOOR_H` 3.4 a storey above). */
const floorY = (floor: number): number => (floor === 0 ? 0.08 : floor * 3.4);

/** Building-local → world (the visit's `toWorld`; three's rotation.y). */
export function frameToWorld(f: Pick<VenueFrame, 'x' | 'z' | 'yaw'>, lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(f.yaw), s = Math.sin(f.yaw);
  return { x: f.x + lx * c + lz * s, z: f.z - lx * s + lz * c };
}
/** World → building-local. */
export function frameToLocal(f: Pick<VenueFrame, 'x' | 'z' | 'yaw'>, x: number, z: number): { lx: number; lz: number } {
  const c = Math.cos(f.yaw), s = Math.sin(f.yaw);
  const dx = x - f.x, dz = z - f.z;
  return { lx: c * dx - s * dz, lz: s * dx + c * dz };
}

function make(f: VenueFrame, role: VenueRole, id: string, floor: number, lx: number, lz: number, facing: number, tlx: number, tlz: number, source: VenueAnchorSource, extra: Partial<WorldAnchor> = {}): WorldAnchor {
  const w = frameToWorld(f, lx, lz), t = frameToWorld(f, tlx, tlz);
  return { role, id, floor, x: w.x, y: f.groundY + floorY(floor), z: w.z, yaw: f.yaw + facing, tx: t.x, tz: t.z, lx, lz, facing, source, ...extra };
}

function fromDesc(f: VenueFrame, role: VenueRole, a: DescAnchor): WorldAnchor {
  let seat: WorldAnchor['seat'];
  if (a.seat) { const s = frameToWorld(f, a.seat.x, a.seat.z); seat = { pose: a.seat.pose, x: s.x, z: s.z, yaw: f.yaw + a.seat.yaw, eye: a.seat.eye }; }
  return make(f, role, a.id, a.floor, a.ax, a.az, a.facing, a.x, a.z, 'descriptor', { seat, piece: a.piece });
}

// ─── Standing room on a floor that has pieces and no anchors ─────────────────────────────────────────────────────────

/** A standing body's radius against a drawn piece (the KCC capsule is 0.3; a hand's width of air). */
const BODY_R = 0.34;
interface Box { x0: number; x1: number; z0: number; z1: number }
function blockers(pieces: readonly DescPiece[]): Box[] {
  const out: Box[] = [];
  for (const p of pieces) {
    if (!p.blocksWalk) continue;
    const cs = pieceCorners(p);
    out.push({ x0: Math.min(...cs.map((c) => c[0])), x1: Math.max(...cs.map((c) => c[0])), z0: Math.min(...cs.map((c) => c[1])), z1: Math.max(...cs.map((c) => c[1])) });
  }
  return out;
}
/** May a body stand at (x, z): inside the shell's clearances (walls, the door corridor, the stair zone) and off every drawn piece? */
export function standable(f: PlanFrame, boxes: readonly Box[], x: number, z: number): boolean {
  if (!slotClear(f, x, z, 0.45)) return false;
  for (const b of boxes) if (x > b.x0 - BODY_R && x < b.x1 + BODY_R && z > b.z0 - BODY_R && z < b.z1 + BODY_R) return false;
  return true;
}

/** The plans' convention: a piece at yaw θ FRONTS toward (sin θ, cos θ). */
const frontOf = (yaw: number): [number, number] => [Math.sin(yaw), Math.cos(yaw)];

/**
 * A service point cut from ONE legacy piece (a counter, a reception desk): the customer in front of it, the staff
 * behind it (else at an end), the queue trailing away from its front. Null when no standing room is found for both.
 */
function legacyService(f: VenueFrame, boxes: readonly Box[], piece: DescPiece, n: number): { staff: WorldAnchor; customer: WorldAnchor; queue: WorldAnchor[] } | null {
  const [fx, fz] = frontOf(piece.yaw);
  const rx = fz, rz = -fx; // the piece's right (local +x turned)
  const hd = piece.half.d, hw = piece.half.w;
  let staff: [number, number] | null = null;
  for (const [back, side] of [[hd + 0.5, 0], [0, hw + 0.55], [0, -(hw + 0.55)], [hd + 0.5, hw * 0.6], [hd + 0.5, -hw * 0.6]] as const) {
    const x = piece.x - fx * back + rx * side, z = piece.z - fz * back + rz * side;
    if (standable(f, boxes, x, z)) { staff = [x, z]; break; }
  }
  let cust: [number, number] | null = null, custDist = 0, custSide = 0;
  search: for (const dist of [hd + 0.6, hd + 1.0, hd + 1.5, hd + 2.1]) for (const side of [0, 0.7, -0.7, 1.4, -1.4]) {
    const x = piece.x + fx * dist + rx * side, z = piece.z + fz * dist + rz * side;
    if (standable(f, boxes, x, z)) { cust = [x, z]; custDist = dist; custSide = side; break search; }
  }
  if (!staff || !cust) return null;
  const queue: WorldAnchor[] = [];
  for (let k = 1; k <= 3 && queue.length < 2; k++) {
    const x = piece.x + fx * (custDist + 0.9 * k) + rx * custSide, z = piece.z + fz * (custDist + 0.9 * k) + rz * custSide;
    if (standable(f, boxes, x, z)) queue.push(make(f, 'queue', `legacy/queue#${n}.${queue.length}`, piece.floor, x, z, piece.yaw + Math.PI, piece.x, piece.z, 'legacy'));
  }
  return {
    staff: make(f, 'staff', `legacy/staff#${n}`, piece.floor, staff[0], staff[1], piece.yaw, piece.x, piece.z, 'legacy', { piece: piece.slot }),
    customer: make(f, 'customer', `legacy/customer#${n}`, piece.floor, cust[0], cust[1], piece.yaw + Math.PI, piece.x, piece.z, 'legacy', { piece: piece.slot }),
    queue,
  };
}

/** A room with no counter: three points down the back half of the room, on the first column that is clear. */
function shellService(f: VenueFrame, boxes: readonly Box[]): WorldAnchor[] {
  for (const sx of [f.hw * 0.3, f.hw * 0.55, 0, -f.hw * 0.25, f.hw * 0.75]) {
    const sz = f.hd - 1.3, cz = f.hd - 2.9;
    if (!standable(f, boxes, sx, sz) || !standable(f, boxes, sx, cz)) continue;
    const out = [
      make(f, 'staff', 'shell/staff#0', 0, sx, sz, Math.PI, sx, f.hd - 2.1, 'shell'),
      make(f, 'customer', 'shell/customer#0', 0, sx, cz, 0, sx, f.hd - 2.1, 'shell'),
    ];
    for (let k = 1; k <= 2; k++) if (standable(f, boxes, sx, cz - 0.9 * k)) out.push(make(f, 'queue', `shell/queue#${k - 1}`, 0, sx, cz - 0.9 * k, 0, sx, f.hd - 2.1, 'shell'));
    return out;
  }
  // nothing clear on any column (a room packed to the walls): the middle of the back half, as the last word
  return [
    make(f, 'staff', 'shell/staff#0', 0, 0, f.hd * 0.5, Math.PI, 0, f.hd * 0.3, 'shell'),
    make(f, 'customer', 'shell/customer#0', 0, 0, f.hd * 0.1, 0, 0, f.hd * 0.3, 'shell'),
  ];
}

/**
 * THE VENUE'S ANCHORS in world space. `d` = the active descriptor (null = none: the plan has not landed, a bespoke
 * furnisher drew the room); `shown` = the slots the visit actually seated on each floor (`SeatView`: a hidden or
 * unpacked piece is no counter) — null = every piece. Ground floor only: the server cannot tell floors apart yet.
 */
export function venueAnchors(kind: string, d: InteriorDescriptor | null, f: VenueFrame, shown: ReadonlySet<string> | null = null): VenueAnchors {
  const live = (a: DescAnchor): boolean => a.floor === 0 && (!a.piece || !shown || shown.has(a.piece));
  const ground = d?.floors.find((q) => q.floor === 0) ?? null;
  if (d && ground) {
    const of = (k: DescAnchor['kind']): DescAnchor[] => anchorsOfKind(d, k).filter(live);
    if (kind === 'clinic') {
      // the cot nearest the entry with a bedside: the healer stands at it, the patient lies on it
      const bedsides = of('bedside');
      const beds = of('bed');
      const pair = bedsides.map((s) => ({ s, b: beds.find((b) => b.piece !== undefined && b.piece === s.piece) })).find((p) => p.b);
      const staff = [...(pair ? [pair.s] : []), ...of('service-staff')].map((a) => fromDesc(f, 'staff', a));
      const customer = [...(pair?.b ? [pair.b] : []), ...of('service-customer')].map((a) => fromDesc(f, 'customer', a));
      if (staff.length && customer.length) return { kind, source: 'descriptor', staff, customer, queue: of('queue').map((a) => fromDesc(f, 'queue', a)) };
    } else {
      const staff = of('service-staff').map((a) => fromDesc(f, 'staff', a));
      const customer = of('service-customer').map((a) => fromDesc(f, 'customer', a));
      if (staff.length && customer.length) return { kind, source: 'descriptor', staff, customer, queue: of('queue').map((a) => fromDesc(f, 'queue', a)) };
    }
    // no anchors on this floor: its own counter / desk / cot, when it has one
    const pieces = ground.pieces.filter((p) => !shown || p.tier === 'owned' || shown.has(p.slot));
    const boxes = blockers(pieces);
    const wanted = kind === 'clinic' ? ['desk_terminal', 'bed_single'] : ['counter_bar', 'desk_terminal'];
    const out: VenueAnchors = { kind, source: 'legacy', staff: [], customer: [], queue: [] };
    for (const key of wanted) {
      const list = pieces.filter((p) => p.key === key && p.tier !== 'owned').sort((a, b) => a.order - b.order);
      let n = 0;
      for (const p of list) {
        const sv = legacyService(f, boxes, p, n);
        if (!sv) continue;
        n++;
        out.staff.push(sv.staff); out.customer.push(sv.customer);
        if (out.queue.length === 0) out.queue.push(...sv.queue);
        if (out.staff.length >= 2) break;
      }
      if (out.staff.length) return out;
    }
    const sh = shellService(f, boxes);
    return { kind, source: 'shell', staff: sh.filter((a) => a.role === 'staff'), customer: sh.filter((a) => a.role === 'customer'), queue: sh.filter((a) => a.role === 'queue') };
  }
  const sh = shellService(f, []);
  return { kind, source: 'shell', staff: sh.filter((a) => a.role === 'staff'), customer: sh.filter((a) => a.role === 'customer'), queue: sh.filter((a) => a.role === 'queue') };
}

// ─── THE COUNTER'S OFFER (one key, one focus): what E means where the player stands ──────────────────────────────────

/** How near the staff side / the customer side a player must stand for the counter to answer E (m). */
export const COUNTER_REACH_M = 1.9;

export interface CounterFacts {
  /** 'The Radiant Cup' / 'the clinic'. */
  venueName: string;
  /** 'publican' / 'healer'. */
  postLabel: string;
  /** I hold THIS venue's post · I hold some other post. */
  mine: boolean; holdOther: boolean;
  /** The player who holds it (not me), when one does. */
  holder: string | null;
  /** The town's own versona on the post, when one is on it now. */
  staff: string | null;
  /** My oldest waiting customer: what they want, and whether they have reached the door. */
  waiting: { need: string; arrived: boolean } | null;
  /** Distance to the nearest staff / customer standing point (Infinity when none); `anywhere` = a room with no counter (serve from where you stand). */
  dStaff: number; dCustomer: number; anywhere: boolean;
}
export interface CounterOffer {
  /** The claim's id in the arbiter (`post:serve` is the Town module's own — kept). */
  id: 'post:serve' | 'venue:take' | 'venue:counter';
  label: string;
  action: 'serve' | 'take' | 'posts';
  dist: number;
}

/** What the counter offers the player right now, or null (nothing truthful to offer from here). */
export function counterOffer(s: CounterFacts): CounterOffer | null {
  const atStaff = s.dStaff <= COUNTER_REACH_M, atCustomer = s.dCustomer <= COUNTER_REACH_M;
  // THE SIDE LAW (2026-10-01, found by the keeper picture): a narrow counter puts BOTH standing points inside each
  // other's reach (1.60 m apart in city 3's cantina against the 1.9 m reach), so reach alone read the customer's side
  // as "behind the counter" and offered the post to a customer facing the town's keeper. Which side you stand on is
  // the NEARER standing point.
  const staffSide = atStaff && s.dStaff <= s.dCustomer;
  if (s.mine) {
    if (s.waiting?.arrived && (atStaff || s.anywhere)) return { id: 'post:serve', label: `E — serve: ${s.waiting.need}`, action: 'serve', dist: s.anywhere ? Math.min(s.dStaff, 1) : s.dStaff };
    return null; // on duty with nobody to serve: the HUD strip says so; the key stays free for the room's furniture
  }
  if (staffSide && !s.holder && !s.holdOther) return { id: 'venue:take', label: `E — take the ${s.postLabel}'s post (you work it from here)`, action: 'take', dist: s.dStaff };
  if (atCustomer || atStaff) {
    const who = s.holder ?? s.staff;
    const keeps = who ? `${who} keeps the ${s.postLabel}'s post` : `nobody keeps the ${s.postLabel}'s post`;
    return { id: 'venue:counter', label: `E — ${s.venueName}: ${keeps}`, action: 'posts', dist: Math.min(s.dStaff, s.dCustomer) };
  }
  return null;
}
